import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { Plus, PencilSimple, Eye, Archive, Trash, X, UploadSimple, BookOpen } from "@phosphor-icons/react";

const EMPTY = { title: "", slug: "", summary: "", body: "", category: "", author: "", status: "Draft", seo_title: "", seo_description: "" };
const STATUS_TONE = { Published: "bg-emerald-100 text-emerald-700", Draft: "bg-slate-100 text-slate-600", Archived: "bg-amber-100 text-amber-700" };

export default function Articles() {
  const [data, setData] = useState({ items: [], categories: [] });
  const [statusF, setStatusF] = useState("");
  const [editing, setEditing] = useState(null); // article object or EMPTY
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/articles", { params: { status: statusF || undefined, limit: 100 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [statusF]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Judul wajib diisi"); return; }
    setBusy(true);
    try {
      const payload = { ...editing };
      let saved;
      if (editing.id) saved = (await api.put(`/articles/${editing.id}`, payload)).data;
      else saved = (await api.post("/articles", payload)).data;
      toast.success("Artikel disimpan");
      setEditing({ ...saved });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setStatus = async (a, status) => {
    try { await api.put(`/articles/${a.id}`, { status }); toast.success(`Status: ${status}`); load(); if (editing?.id === a.id) setEditing({ ...editing, status }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const archive = async (a) => {
    if (!window.confirm(a.status === "Draft" ? "Hapus draft ini?" : "Arsipkan artikel ini?")) return;
    try { await api.delete(`/articles/${a.id}`); toast.success("Selesai"); load(); if (editing?.id === a.id) setEditing(null); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file || !editing?.id) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data: r } = await api.post(`/articles/${editing.id}/cover`, fd); setEditing({ ...editing, cover_file_id: r.cover_file_id }); toast.success("Cover diunggah"); load(); }
    catch (er) { toast.error(apiError(er)); }
  };
  const removeCover = async () => {
    try { await api.delete(`/articles/${editing.id}/cover`); setEditing({ ...editing, cover_file_id: null }); toast.success("Cover dihapus"); load(); }
    catch (er) { toast.error(apiError(er)); }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="articles-admin">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-800"><BookOpen size={26} weight="duotone" className="text-[#7C4DE0]" /> Artikel &amp; Edukasi</h1>
        <button data-testid="article-new" onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-1.5 rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]"><Plus size={16} weight="bold" /> Artikel Baru</button>
      </div>

      <div className="mb-3 flex gap-2">
        {["", "Draft", "Published", "Archived"].map((s) => (
          <button key={s || "all"} data-testid={`filter-${s || "all"}`} onClick={() => setStatusF(s)} className={`rounded-full px-3 py-1 text-xs font-bold ${statusF === s ? "bg-slate-800 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{s || "Semua"}</button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-rose-100 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-100 text-xs text-slate-500"><tr><th className="px-3 py-2">Judul</th><th className="px-3 py-2">Kategori</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Publikasi</th><th className="px-3 py-2 text-right">Aksi</th></tr></thead>
          <tbody data-testid="article-rows">
            {data.items.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Belum ada artikel.</td></tr>}
            {data.items.map((a) => (
              <tr key={a.id} className="border-b border-slate-50 last:border-0">
                <td className="px-3 py-2 font-semibold text-slate-800">{a.title}<div className="text-[11px] font-normal text-slate-400">/{a.slug}</div></td>
                <td className="px-3 py-2 text-slate-500">{a.category || "-"}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_TONE[a.status]}`}>{a.status}</span></td>
                <td className="px-3 py-2 text-xs text-slate-400">{a.published_at ? String(a.published_at).slice(0, 10) : "-"}</td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`edit-${a.slug}`} onClick={() => setEditing({ ...a })} className="rounded-lg bg-slate-100 p-1.5 text-slate-600 hover:bg-slate-200" title="Edit"><PencilSimple size={15} /></button>
                    <button onClick={() => setPreview(a)} className="rounded-lg bg-slate-100 p-1.5 text-slate-600 hover:bg-slate-200" title="Preview"><Eye size={15} /></button>
                    {a.status !== "Published" && <button data-testid={`publish-${a.slug}`} onClick={() => setStatus(a, "Published")} className="rounded-lg bg-emerald-100 p-1.5 text-emerald-700 hover:bg-emerald-200" title="Terbitkan"><UploadSimple size={15} /></button>}
                    <button data-testid={`archive-${a.slug}`} onClick={() => archive(a)} className="rounded-lg bg-amber-100 p-1.5 text-amber-700 hover:bg-amber-200" title={a.status === "Draft" ? "Hapus draft" : "Arsipkan"}>{a.status === "Draft" ? <Trash size={15} /> : <Archive size={15} />}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3" data-testid="article-editor">
          <div className="my-6 w-full max-w-2xl rounded-2xl bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-lg font-extrabold text-slate-800">{editing.id ? "Edit Artikel" : "Artikel Baru"}</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <Input label="Judul *" testid="art-title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <Input label="Slug (otomatis, dapat diedit)" testid="art-slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} placeholder="dibuat-dari-judul" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Kategori" testid="art-category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
                <Input label="Penulis" testid="art-author" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} />
              </div>
              <Area label="Ringkasan" testid="art-summary" rows={2} value={editing.summary} onChange={(v) => setEditing({ ...editing, summary: v })} />
              <Area label="Isi Artikel (teks biasa, mendukung baris baru)" testid="art-body" rows={8} value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="SEO Title" testid="art-seo-title" value={editing.seo_title} onChange={(v) => setEditing({ ...editing, seo_title: v })} />
                <Input label="SEO Description" testid="art-seo-desc" value={editing.seo_description} onChange={(v) => setEditing({ ...editing, seo_description: v })} />
              </div>
              {editing.id ? (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-2">
                  {editing.cover_file_id ? <img src={`${API}/public/articles/${editing.id}/cover?t=${Date.now()}`} alt="cover" className="h-16 w-24 rounded-lg object-cover" /> : <div className="flex h-16 w-24 items-center justify-center rounded-lg bg-slate-100 text-slate-400"><BookOpen size={20} /></div>}
                  <div className="flex gap-2">
                    <label className="cursor-pointer rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200"><input data-testid="art-cover" type="file" accept="image/*" className="hidden" onChange={uploadCover} />Unggah Cover</label>
                    {editing.cover_file_id && <button onClick={removeCover} className="rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-bold text-rose-600">Hapus Cover</button>}
                  </div>
                </div>
              ) : <p className="text-xs text-slate-400">Simpan dulu untuk mengunggah cover.</p>}
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <span className={`mr-auto self-center rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_TONE[editing.status]}`}>{editing.status}</span>
              <button data-testid="art-save" disabled={busy} onClick={save} className="rounded-xl bg-slate-700 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800">Simpan</button>
              {editing.id && editing.status !== "Published" && <button data-testid="art-publish" disabled={busy} onClick={() => setStatus(editing, "Published")} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700">Terbitkan</button>}
              {editing.id && editing.status === "Published" && <button disabled={busy} onClick={() => setStatus(editing, "Draft")} className="rounded-xl bg-amber-100 px-4 py-2 text-sm font-bold text-amber-700">Jadikan Draft</button>}
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3" data-testid="article-preview" onClick={() => setPreview(null)}>
          <div className="my-6 w-full max-w-2xl rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPreview(null)} className="float-right rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X size={20} /></button>
            {preview.category && <span className="rounded-full bg-[#EEE6FF] px-2 py-0.5 text-xs font-bold text-[#7C4DE0]">{preview.category}</span>}
            <h1 className="mt-2 font-heading text-2xl font-extrabold text-slate-800">{preview.title}</h1>
            {preview.summary && <p className="mt-2 font-medium text-slate-600">{preview.summary}</p>}
            <div className="mt-3 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-slate-700">{preview.body}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid, placeholder }) {
  return (<label className="block"><span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span><input data-testid={testid} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#7C4DE0]" /></label>);
}
function Area({ label, value, onChange, testid, rows }) {
  return (<label className="block"><span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span><textarea data-testid={testid} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#7C4DE0]" /></label>);
}
